import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import { Watch } from '@Ravnur-Inc/decorators';
import { Vue, Options, prop, setup } from 'vue-class-component';
import { languagesService } from '@/modules/common-services';
import useSecurityStore from '@/store/security';

import { $t, liveSourcesService } from '../live-facade';

import {
  LiveProcessing$Settings,
  LiveSource,
  isRMSLiveSourceType,
  LiveChannel$Indexing,
  DEFAULT_GENERATE_CAPTIONS_LANGUAGE_CODE,
  LiveSource$Encoding,
  LiveSource$Type,
} from '../types/Live';

import './live-processing-settings.scss';

const CN = 'live-processing-settings';

class Props {
  settings: LiveProcessing$Settings;
  sourceId: Nullable<string>;
  direction = prop<'row' | 'column'>({ default: 'column' });
}

type Emits = {
  onChange: (settings: LiveProcessing$Settings) => void;
};

@Options({
  emits: ['change'],
})
export default class LiveProcessingSettings extends Vue.with(Props) {
  declare $props: $Props<Props, Emits>;

  security = setup(() => useSecurityStore());

  languageId: Nullable<string> = null;

  private get source(): Nullable<LiveSource> {
    const { sourceId } = this;
    const { cache } = liveSourcesService;
    return sourceId ? cache[sourceId] : null;
  }

  private get isAutoCaptionsAvailable() {
    return this.security.isAutoCaptionsAvailable;
  }

  private get languages() {
    return languagesService.list.filter((item) => item.indexable);
  }

  private get isRMSSourceSelected() {
    const { source } = this;
    return source ? isRMSLiveSourceType(source) : false;
  }

  private get isMUXSourceSelected() {
    const { source } = this;
    return source ? source.type === LiveSource$Type.MUX : false;
  }

  protected get publishImmediately() {
    return this.settings.publishImmediately;
  }

  protected set publishImmediately(publishImmediately: boolean) {
    this.change({ ...this.settings, publishImmediately });
  }

  protected get isIndexing() {
    return !!(this.settings.indexing && this.settings.indexing !== LiveChannel$Indexing.NONE);
  }

  protected set isIndexing(value: boolean) {
    this.change({
      ...this.settings,
      languageId: value ? this.languageId : null,
      indexing: value ? LiveChannel$Indexing.AUDIO : LiveChannel$Indexing.NONE,
    });
  }

  protected get notConvertToVOD(): boolean {
    return this.settings.notConvertToVOD || false;
  }

  protected set notConvertToVOD(value: boolean) {
    this.change({
      ...this.settings,
      notConvertToVOD: value,
    });
  }

  get isABRSource() {
    const abrEncodings = [LiveSource$Encoding.PREMIUM, LiveSource$Encoding.STANDARD];
    return this.source?.encoding ? abrEncodings.includes(this.source.encoding) : false;
  }

  get isMUXSource() {
    return this.source?.type === LiveSource$Type.MUX;
  }

  @Watch('notConvertToVOD')
  onConvertToVODChanged() {
    if (this.notConvertToVOD) {
      this.isIndexing = false;
    }
  }

  @Watch('languages', { immediate: true })
  onLanguageListChange() {
    this.setLanguageId();
  }

  @Watch('languageId', { immediate: true })
  onLanguageChange() {
    this.change({
      ...this.settings,
      languageId: this.languageId,
    });
  }

  @Watch('isIndexing')
  onIndexingChange(value: boolean) {
    if (value) {
      this.setLanguageId();
    }
  }

  @Watch('isRMSSourceSelected')
  protected handleSourceChanged(isRMSSource: boolean) {
    const { source, settings } = this;
    if (!source) {
      return;
    }
    if (isRMSSource) {
      if (settings && !settings.indexing) {
        this.change({
          ...this.settings,
          indexing: LiveChannel$Indexing.AUDIO,
          publishImmediately: false,
        });
      }
    } else {
      this.change({ ...this.settings, indexing: null, publishImmediately: false });
    }
  }

  created() {
    liveSourcesService.load();
  }

  render() {
    if (!this.isRMSSourceSelected && !this.isMUXSourceSelected) {
      return;
    }

    return (
      <div class={[CN, `${CN}--${this.direction}`]}>
        {this.isAutoCaptionsAvailable && (
          <>
            <checkbox
              class={`${CN}__field`}
              disabled={this.notConvertToVOD}
              label={$t('live-channels', 'field__generate-captions')}
              vModel={[this.isIndexing, 'value']}
            />
            {this.isIndexing && (
              <chosen
                class={`${CN}__field`}
                keyForLabel="localName"
                label={$t('live-channels', 'field__language')}
                options={
                  this.isMUXSource
                    ? this.languages.filter((item) => item.abbreviation === 'en-US')
                    : this.languages
                }
                vModel={[this.languageId, 'value']}
              />
            )}
          </>
        )}

        <checkbox
          class={`${CN}__field`}
          label={$t('live-channels', 'field__publish-immediately')}
          vModel={[this.publishImmediately, 'value']}
        />

        {'notConvertToVOD' in this.settings && (!this.isABRSource || this.isMUXSource) && (
          <checkbox
            class={`${CN}__field`}
            label={this.$t('live-events', 'field__do-not-convert-to-vod')}
            vModel={[this.notConvertToVOD, 'value']}
          />
        )}
      </div>
    );
  }

  private change(settings: LiveProcessing$Settings) {
    this.$emit('change', settings);
  }

  private setLanguageId() {
    if (!this.isIndexing) {
      return;
    }

    const languageId =
      this.languages.find((item) => item.abbreviation === DEFAULT_GENERATE_CAPTIONS_LANGUAGE_CODE)
        ?.id ?? null;
    this.languageId = this.settings.languageId || languageId;
  }
}
